import React, { useState, useEffect, useContext } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../../context/UserContext';
import { NotificationContext } from '../../context/NotificationContext';
import { apiFetch } from '../../utils/apiClient';
import { BASE_URL } from '../../utils/config';
import './InvestmentProfileModal.css';

const InvestmentProfileModal = ({ isOpen, onClose, initialProfileData = {}, onSave, mode = 'fill' }) => {
  const { user, updateUser } = useContext(UserContext);
  const { addNotification } = useContext(NotificationContext);
  const { t } = useTranslation();
  const [profileData, setProfileData] = useState(initialProfileData);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // When the modal opens, sync its state with the initial data provided.
    const dataWithDefaults = { ...(initialProfileData || {}) };

    // Ensure multi-select fields are arrays
    if (!Array.isArray(dataWithDefaults.investmentStrategy)) {
      dataWithDefaults.investmentStrategy = [];
    }
    if (!Array.isArray(dataWithDefaults.financingMethod)) {
      dataWithDefaults.financingMethod = [];
    }

    if (dataWithDefaults.desiredInvolvement == null) {
      dataWithDefaults.desiredInvolvement = 5;
    }
    setProfileData(dataWithDefaults);
  }, [isOpen, initialProfileData]);

  const handleChange = (e) => {
    const { name, value, checked } = e.target;

    if (name === 'investmentStrategy' || name === 'financingMethod') {
      // Handle checkbox group state
      setProfileData((prev) => {
        const currentValues = prev[name] || [];
        if (checked) {
          // Add value to the array if it's not already there
          return { ...prev, [name]: [...currentValues, value] };
        } else {
          // Remove value from the array
          return { ...prev, [name]: currentValues.filter((item) => item !== value) };
        }
      });
    } else {
      // Handle other input types (select, range, etc.)
      setProfileData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await apiFetch(
        `${BASE_URL}/authUser/profile`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(profileData),
        },
        user.token
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save profile.');
      }

      const savedData = await response.json();

      // To ensure all components consuming the context are properly updated,
      // we create a new user object by merging the existing user state
      // with the newly saved investment profile.
      updateUser({
        ...user,
        investmentProfile: savedData.investmentProfile,
      });

      addNotification(t('investmentProfileModal.saveSuccess'), 'success');
      if (onSave) {
        onSave(savedData.investmentProfile); // Pass the updated profile back
      }
      onClose(); // Close the modal on success
    } catch (error) {
      addNotification(t('investmentProfileModal.saveError', { error: error.message }), 'error');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const title = mode === 'confirm' ? t('investmentProfileModal.titleConfirm') : t('investmentProfileModal.titleFill');
  const buttonText = mode === 'confirm' ? t('investmentProfileModal.buttonConfirm') : t('investmentProfileModal.buttonSave');
  const introText = mode === 'confirm' ? t('investmentProfileModal.introConfirm') : t('investmentProfileModal.introFill');

  const investmentStrategyOptions = [
    { value: 'Long-Term Rental', label: t('investmentProfileModal.strategy_longTerm') },
    { value: 'Short-Term Flip', label: t('investmentProfileModal.strategy_flip') },
    { value: 'BRRRR', label: t('investmentProfileModal.strategy_brrrr') },
    { value: 'Undecided / Exploring Options', label: t('investmentProfileModal.strategy_undecided') },
  ];

  const financingMethodOptions = [
    { value: 'All Cash', label: t('investmentProfileModal.financing_cash') },
    { value: 'Conventional Mortgage', label: t('investmentProfileModal.financing_conventional') },
    { value: 'Hard Money / Private Loan', label: t('investmentProfileModal.financing_hardMoney') },
    { value: 'Owner Finance', label: t('investmentProfileModal.financing_owner') },
    { value: 'Exploring Options', label: t('investmentProfileModal.financing_exploring') },
  ];
  return ReactDOM.createPortal(
    <div className="investment-profile-modal-overlay" onClick={onClose}>
      <div className="investment-profile-modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="close-btn" onClick={onClose} aria-label={t('investmentProfileModal.close_aria_label')}>
          <i className="fas fa-times"></i>
        </button>
        <h2>{title}</h2>
        <p className="intro-text">{introText}</p>
        <form onSubmit={handleSave}>
          <div className="form-row">
            {/* Investment Strategy */}
            <div className="form-group">
              <label>{t('investmentProfileModal.investmentStrategy')}</label>
              <div className="checkbox-group">
                {investmentStrategyOptions.map((option) => (
                  <div key={option.value} className="checkbox-item">
                    <input type="checkbox" id={`strategy-${option.value}`} name="investmentStrategy" value={option.value} checked={profileData.investmentStrategy?.includes(option.value) || false} onChange={handleChange} />
                    <label htmlFor={`strategy-${option.value}`}>{option.label}</label>
                  </div>
                ))}
              </div>
            </div>

            {/* Financing Method */}
            <div className="form-group">
              <label>{t('investmentProfileModal.financingMethod')}</label>
              <div className="checkbox-group">
                {financingMethodOptions.map((option) => (
                  <div key={option.value} className="checkbox-item">
                    <input type="checkbox" id={`financing-${option.value}`} name="financingMethod" value={option.value} checked={profileData.financingMethod?.includes(option.value) || false} onChange={handleChange} />
                    <label htmlFor={`financing-${option.value}`}>{option.label}</label>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Investment Budget */}
          <div className="form-group horizontal">
            <label>{t('investmentProfileModal.investmentBudget')}</label>
            <select name="investmentBudget" value={profileData.investmentBudget || ''} onChange={handleChange} required>
              <option value="" disabled>
                {t('investmentProfileModal.budget_placeholder')}
              </option>
              <option value="Under $100,000">{t('investmentProfileModal.budget_under100k')}</option>
              <option value="$100,000 - $250,000">{t('investmentProfileModal.budget_100k_250k')}</option>
              <option value="$250,000 - $500,000">{t('investmentProfileModal.budget_250k_500k')}</option>
              <option value="$500,000+">{t('investmentProfileModal.budget_over500k')}</option>
            </select>
          </div>

          {/* Experience Level */}
          <div className="form-group horizontal">
            <label>{t('investmentProfileModal.experienceLevel')}</label>
            <select name="experienceLevel" value={profileData.experienceLevel || ''} onChange={handleChange} required>
              <option value="" disabled>
                {t('investmentProfileModal.experience_placeholder')}
              </option>
              <option value="This is my first one">{t('investmentProfileModal.experience_first')}</option>
              <option value="1-2 properties">{t('investmentProfileModal.experience_1_2')}</option>
              <option value="3-5 properties">{t('investmentProfileModal.experience_3_5')}</option>
              <option value="6-10 properties">{t('investmentProfileModal.experience_6_10')}</option>
              <option value="11+ properties">{t('investmentProfileModal.experience_11_plus')}</option>
            </select>
          </div>

          {/* Purchase Timeline */}
          <div className="form-group horizontal">
            <label>{t('investmentProfileModal.purchaseTimeline')}</label>
            <select name="purchaseTimeline" value={profileData.purchaseTimeline || ''} onChange={handleChange} required>
              <option value="" disabled>
                {t('investmentProfileModal.timeline_placeholder')}
              </option>
              <option value="Immediately">{t('investmentProfileModal.timeline_immediately')}</option>
              <option value="Within 3 months">{t('investmentProfileModal.timeline_3_months')}</option>
              <option value="Within 6 months">{t('investmentProfileModal.timeline_6_months')}</option>
              <option value="Just Browsing">{t('investmentProfileModal.timeline_browsing')}</option>
            </select>
          </div>

          {/* Desired Involvement */}
          <div className="form-group horizontal">
            <label htmlFor="desiredInvolvement">
              {t('investmentProfileModal.desiredInvolvement')}
              <span className="label-subtitle">{t('investmentProfileModal.involvement_subtitle')}</span>
            </label>
            <div>
              <div className="slider-container">
                <span>1</span>
                <input type="range" id="desiredInvolvement" name="desiredInvolvement" min="1" max="10" value={profileData.desiredInvolvement || 5} onChange={handleChange} required />
                <span>10</span>
              </div>
              <div className="slider-value">{profileData.desiredInvolvement || 5}</div>
            </div>
          </div>

          <div className="button-group">
            <button type="button" className="cancel-btn" onClick={onClose} disabled={isLoading}>
              {t('investmentProfileModal.buttonCancel')}
            </button>
            <button type="submit" className="save-btn" disabled={isLoading}>
              {isLoading ? t('investmentProfileModal.buttonSaving') : buttonText}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default InvestmentProfileModal;
